interface GetSocureErrorReportParams {
  thrown: unknown
  /** Constant message for the logged Error when the SDK rejects with a bare string instead of an Error. */
  fallbackMessage: string
}

interface SocureErrorContext extends Record<string, unknown> {
  socure: {
    reason: string
    causeChain?: string[]
    requestUrl?: string
    responseStatus?: number
    responseBody?: unknown
  }
}

export interface SocureErrorReport {
  error: Error
  context: SocureErrorContext
}

interface SocureFetchDetails {
  requestUrl?: string
  responseStatus: number
  responseBody?: unknown
}

// Real SDK chains are 2-3 levels deep.
const MAX_CAUSE_DEPTH = 5

const toReason = (value: unknown): string => (value instanceof Error ? value.message : String(value))

const getCause = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return undefined
  }

  if ('cause' in value) {
    return value.cause
  }

  // The SDK's Error subclasses are downlevelled to ES5, which loses the subclass prototype: the `cause`
  // getter is unreachable and the constructor argument survives only as this own property.
  return '_cause' in value ? value._cause : undefined
}

const extractFetchDetails = (value: unknown): SocureFetchDetails | undefined => {
  if (typeof value !== 'object' || value === null || !('response' in value)) {
    return undefined
  }

  const { response } = value

  if (typeof response !== 'object' || response === null || !('status' in response)) {
    return undefined
  }

  if (typeof response.status !== 'number') {
    return undefined
  }

  const request = 'request' in value ? value.request : undefined
  const requestUrl =
    typeof request === 'object' && request !== null && 'url' in request && typeof request.url === 'string'
      ? request.url
      : undefined

  return {
    requestUrl,
    responseStatus: response.status,
    responseBody: 'errResponse' in response ? response.errResponse : undefined,
  }
}

/**
 * Normalizes a value caught from the Socure device-risk SDK into a loggable Error plus RUM context.
 * The SDK nests the failed ingestion fetch (URL, HTTP status, parsed response body) inside its error
 * chain and sometimes rejects with bare strings; this surfaces those diagnostics instead of dropping
 * them. A thrown Error is passed through untouched so existing error-tracking fingerprints stay stable.
 */
export const getSocureErrorReport = ({ thrown, fallbackMessage }: GetSocureErrorReportParams): SocureErrorReport => {
  const error = thrown instanceof Error ? thrown : new Error(fallbackMessage)

  try {
    const causeChain: string[] = []
    const seen = new Set<unknown>([thrown])
    let fetchDetails = extractFetchDetails(thrown)
    let cause = getCause(thrown)

    while (cause !== undefined && cause !== null && !seen.has(cause) && causeChain.length < MAX_CAUSE_DEPTH) {
      seen.add(cause)
      causeChain.push(toReason(cause))
      fetchDetails ??= extractFetchDetails(cause)
      cause = getCause(cause)
    }

    return {
      error,
      context: {
        socure: {
          reason: toReason(thrown),
          ...(causeChain.length > 0 && { causeChain }),
          ...fetchDetails,
        },
      },
    }
  } catch {
    // An error reporter must never throw — callers invoke it inside their own catch blocks, and a
    // throw here would break their fail-open contract (e.g. property getters on a foreign error
    // object may fault when read).
    return { error, context: { socure: { reason: fallbackMessage } } }
  }
}
